import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { COUNT_PROBE_PAGE_SIZE } from '../../../Consts/backoffice.const';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  TenantUserListResponse,
  TenantUserShowResponse,
} from '../../../Interfaces/api/backoffice/user.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';
import { TenantUser } from '../../../Models/backoffice/tenant-user.model';
import { UserFilters } from '../Interfaces/user-filters.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    filters: Partial<UserFilters>,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<TenantUserListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<TenantUserListResponse>>(
      endpoints.backoffice.users,
      { params: this._params(filters, page, pageSize) },
    );
  }

  countRequest(): TypedResourceRequest<LaravelDefaultResponse<TenantUserListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<TenantUserListResponse>>(
      endpoints.backoffice.users,
      { params: { page: 0, pageSize: COUNT_PROBE_PAGE_SIZE }, skipLoading: true },
    );
  }

  showRequest(
    userId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<TenantUserShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<TenantUserShowResponse>>(
      endpoints.backoffice.user(userId),
    );
  }

  toPagedResult(
    response: LaravelDefaultResponse<TenantUserListResponse> | undefined,
  ): PagedResult<TenantUser> {
    const payload = response?.data;

    return {
      items: (payload?.users ?? []).map((row) => new TenantUser(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toDetail(
    response: LaravelDefaultResponse<TenantUserShowResponse> | undefined,
  ): TenantUser | null {
    return response?.data?.user ? new TenantUser(response.data.user) : null;
  }

  private _params(
    filters: Partial<UserFilters>,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== null && value !== undefined && value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
